import { useFormikContext } from 'formik';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DefaultButton from '@components/Button/button';
import { Turnstile } from '@components/Turnstile/Turnstile';
import { type YesNoBoolean } from '@customTypes/form';
import { CheckboxField } from '@pages/order/components/common/CheckboxField';
import InputField from '@pages/order/components/common/InputField';
import { RadioButtonField } from '@pages/order/components/common/RadioButtonField';
import { INVALID_FIELD_SELECTOR, YES_NO_OPTIONS } from '@utils/forms';

import type { HcpReferralFormValues } from '../HcpReferral.validation';
import { FileUpload } from './FileUpload';

const FORM_LEGAL =
  "By submitting, you confirm you have authorization to share this report on the patient's behalf. " +
  'All PHI is handled under HIPAA.';

const ELIGIBILITY_ITEMS = [
  {
    id: 'is_us_resident',
    label: 'Lives in the United States',
  },
  {
    id: 'has_detected_variant',
    label: (
      <>
        Clinical genetic testing report identifying a <i>CASR</i> gene variant
      </>
    ),
  },
] as const;

const CLINICAL_HISTORY_ITEMS = [
  { id: 'low_blood_calcium', label: 'History of low blood calcium?' },
  { id: 'elevated_urine_calcium', label: 'History of elevated urine calcium?' },
  { id: 'adh1_symptoms', label: 'Signs or symptoms consistent with ADH1?' },
  { id: 'adh1_diagnosis', label: 'Clinical diagnosis of ADH1?' },
] as const;

const FILE_UPLOAD_ERROR_SELECTOR = '.file-upload:has(.file-upload__error)';

interface FormSectionProps {
  number: number;
  title: string;
  subtitle: string;
  children: ReactNode;
}

// Two-column section: number + heading + subtitle on the left, inputs on the right.
// Below md (≤767px) the meta stacks above the fields so the form remains usable on phones.
const FormSection = ({ number, title, subtitle, children }: FormSectionProps) => (
  <section className='intake-form__section'>
    <Row className='g-3 g-md-4'>
      <Col xs={12} md={4} className='intake-form__section-meta'>
        <div className='intake-form__section-number-row'>
          <span className='intake-form__section-number' aria-hidden='true'>
            {number}
          </span>
          <h2 className='intake-form__section-title'>{title}</h2>
        </div>
        <p className='intake-form__section-subtitle'>{subtitle}</p>
      </Col>
      <Col xs={12} md={8} className='intake-form__section-fields'>
        {children}
      </Col>
    </Row>
  </section>
);

export const IntakeForm = () => {
  const { errors, isSubmitting, submitCount, touched, values, handleSubmit, setFieldTouched, setFieldValue } =
    useFormikContext<HcpReferralFormValues>();

  const prevSubmitCountRef = useRef(0);

  useEffect(() => {
    if (submitCount <= prevSubmitCountRef.current) {
      return;
    }

    prevSubmitCountRef.current = submitCount;

    const timeoutId = window.setTimeout(() => {
      const target = document.querySelector(`${INVALID_FIELD_SELECTOR}, ${FILE_UPLOAD_ERROR_SELECTOR}`);

      if (!target) {
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const targetInput = target.querySelector('input:not([type="file"])');

      if (targetInput instanceof HTMLInputElement) {
        targetInput.focus({ preventScroll: true });
      }
    }, 10);

    return () => window.clearTimeout(timeoutId);
  }, [submitCount]);

  // Errors should only render once the field is touched OR the user has attempted submit at least
  // once. This avoids prematurely flagging fields the user hasn't visited yet.
  const shouldShowError = (name: keyof HcpReferralFormValues) => !!touched[name] || submitCount > 0;

  const reportError =
    shouldShowError('report_pdf') && typeof errors.report_pdf === 'string' ? errors.report_pdf : undefined;

  const handleFileChange = (file: File | null) => {
    setFieldTouched('report_pdf', true, false);
    setFieldValue('report_pdf', file, true);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    handleSubmit(event);
  };

  return (
    <Form noValidate className='intake-form' onSubmit={onSubmit}>
      <FormSection
        number={1}
        title='Patient'
        subtitle="We'll only reach out to the patient after our team confirms they're eligible."
      >
        <div className='form-grid'>
          <div className='field'>
            <InputField
              id='patient_first_name'
              label='Patient Legal First Name'
              placeholder='First legal name'
              required
              value={values.patient_first_name}
              onChange={val => setFieldValue('patient_first_name', val)}
              onBlur={() => setFieldTouched('patient_first_name', true)}
              showError={shouldShowError('patient_first_name')}
              error={errors.patient_first_name}
              phiSanitize
            />
          </div>
          <div className='field'>
            <InputField
              id='patient_last_name'
              label='Patient Legal Last Name'
              placeholder='Last legal name'
              required
              value={values.patient_last_name}
              onChange={val => setFieldValue('patient_last_name', val)}
              onBlur={() => setFieldTouched('patient_last_name', true)}
              showError={shouldShowError('patient_last_name')}
              error={errors.patient_last_name}
              phiSanitize
            />
          </div>
          <div className='field field-span-2'>
            <InputField
              id='patient_email'
              label='Patient email'
              required
              type='email'
              placeholder='name@example.com'
              value={values.patient_email}
              onChange={val => setFieldValue('patient_email', val)}
              onBlur={() => setFieldTouched('patient_email', true)}
              showError={shouldShowError('patient_email')}
              error={errors.patient_email}
              phiSanitize
            />
          </div>
        </div>
      </FormSection>

      <FormSection
        number={2}
        title='Patient report'
        subtitle={
          'A single PDF of the genetic testing or clinical report. We read and index it on intake, ' +
          'and store it securely under HIPAA.'
        }
      >
        <FileUpload value={values.report_pdf} onChange={handleFileChange} externalError={reportError} />
      </FormSection>

      <FormSection
        number={3}
        title='Eligibility'
        subtitle='Confirm the patient meets program criteria. The clinical history questions are optional and help our curators prioritize review.'
      >
        <div className='intake-form__eligibility-columns'>
          <div className='intake-form__eligibility-list'>
            <h3 className='intake-form__group-heading'>Patient eligibility (required)</h3>

            {ELIGIBILITY_ITEMS.map(item => {
              const fieldKey = `eligibility.${item.id}`;

              return (
                <CheckboxField
                  key={item.id}
                  checked={values.eligibility?.[item.id]}
                  error={errors.eligibility?.[item.id]}
                  id={item.id}
                  label={item.label}
                  name={item.id}
                  required
                  showError={touched.eligibility?.[item.id] || submitCount > 0}
                  onChange={val => {
                    setFieldTouched(fieldKey, true, false);
                    setFieldValue(fieldKey, val, true);
                  }}
                />
              );
            })}
          </div>

          <div className='intake-form__history-list'>
            <h3 className='intake-form__group-heading'>Patient clinical history (optional)</h3>

            {CLINICAL_HISTORY_ITEMS.map(item => {
              const fieldKey = `clinical_history.${item.id}`;

              return (
                <RadioButtonField<YesNoBoolean>
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  options={YES_NO_OPTIONS}
                  value={values.clinical_history?.[item.id]}
                  onChange={val => {
                    setFieldTouched(fieldKey, true, false);
                    setFieldValue(fieldKey, val, true);
                  }}
                />
              );
            })}
          </div>
        </div>
      </FormSection>

      <FormSection
        number={4}
        title='About you'
        subtitle={
          "For contact if there's an issue with this submission. If you don't give us this information, " +
          "we won't be able to reach you."
        }
      >
        <div className='form-grid'>
          <div className='field'>
            <InputField
              id='hcp_first_name'
              label='Your first name'
              placeholder='First name'
              value={values.hcp_first_name}
              onChange={val => setFieldValue('hcp_first_name', val)}
              onBlur={() => setFieldTouched('hcp_first_name', true)}
              showError={shouldShowError('hcp_first_name')}
              error={errors.hcp_first_name}
              phiSanitize
            />
          </div>
          <div className='field'>
            <InputField
              id='hcp_last_name'
              label='Your last name'
              placeholder='Last name'
              value={values.hcp_last_name}
              onChange={val => setFieldValue('hcp_last_name', val)}
              onBlur={() => setFieldTouched('hcp_last_name', true)}
              showError={shouldShowError('hcp_last_name')}
              error={errors.hcp_last_name}
              phiSanitize
            />
          </div>
          <div className='field field-span-2'>
            <InputField
              id='hcp_email'
              label='Your email'
              type='email'
              placeholder='name@example.com'
              value={values.hcp_email}
              onChange={val => setFieldValue('hcp_email', val)}
              onBlur={() => setFieldTouched('hcp_email', true)}
              showError={shouldShowError('hcp_email')}
              error={errors.hcp_email}
              phiSanitize
            />
            <p className='intake-form__field-helper text-muted'>
              Optional. If provided, we&apos;ll use it to reach you about any issues processing the patient&apos;s
              report and to let you know when processing is complete.
            </p>
          </div>
        </div>
      </FormSection>

      <div className='intake-form__footer'>
        <Turnstile execution='execute' />
        <p className='intake-form__legal'>{FORM_LEGAL}</p>
        <div className='intake-form__submit-wrap'>
          <DefaultButton
            buttonLabel={isSubmitting ? 'Submitting…' : 'Submit referral'}
            styleClass='primaryButtonStyling'
            type='submit'
            // No-op onClick because the `<button type='submit'>` inside `<Form>` already
            // triggers the form's onSubmit handler — but DefaultButton declares onClick
            // as a required prop, so we satisfy the type contract here.
            onClick={() => {}}
            disabled={isSubmitting}
            dataTestId='hcp-referral-submit'
            iconRight={<FontAwesomeIcon icon={faArrowRight} />}
          />
        </div>
      </div>
    </Form>
  );
};
